import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const chapters = [
  {
    num: "01",
    title: "Before You Build",
    desc: "The decisions that shape everything that follows.",
    topics: ["Domain ownership & independent registrars", "Hosting types and how to choose", "Planning site structure before coding", "Honest year-one and year-three cost breakdowns", "Platform selection based on ownership & long-term goals"],
  },
  {
    num: "02",
    title: "HTML and CSS From Scratch",
    desc: "Build a complete hand-coded, mobile-responsive website.",
    topics: ["How HTML and CSS work together", "Semantic markup and document structure", "The Box Model, Flexbox & Grid", "Responsive design and typography", "Forms, validation, and code publishing"],
  },
  {
    num: "03",
    title: "WordPress Setup",
    desc: "From installation to a correctly configured, launch-ready site.",
    topics: ["Installation and essential settings", "HTTPS, permalinks, and theme selection", "Lean plugin strategy and SEO setup", "XML sitemaps and Google Search Console", "Backups, security hardening, and pre-launch audit"],
  },
];

const bigOutcome = [
  { icon: FiIcons.FiShield, label: "Ownership", desc: "Your domain, your files, your data — no platform dependence." },
  { icon: FiIcons.FiEye, label: "Accessibility", desc: "WCAG 2.2 principles built in from the start, not bolted on later." },
  { icon: FiIcons.FiTrendingUp, label: "Search Readiness", desc: "Metadata, sitemaps, and Search Console configured correctly." },
  { icon: FiIcons.FiZap, label: "Performance", desc: "Lean, fast, and built on standards that hold up over time." },
];

export default function Learn() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">What You'll Learn</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          A structured path from absolute zero to a fully functional, custom-built website — built on a foundation you fully own and understand.
        </p>
      </div>

      <div className="space-y-8 mb-20">
        {chapters.map((chapter, idx) => (
          <motion.div key={idx} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-md transition-shadow">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <span className="text-6xl font-black text-slate-100 leading-none">{chapter.num}</span>
              </div>
              <div className="flex-grow">
                <h3 className="text-2xl font-bold text-slate-900 mb-1">{chapter.title}</h3>
                <p className="text-slate-500 mb-5">{chapter.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-2.5">
                  {chapter.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-slate-900 rounded-3xl p-10 md:p-12 text-white mb-12">
        <h2 className="text-2xl font-bold mb-3 text-center">The Bigger Outcome</h2>
        <p className="text-slate-400 text-center mb-10 max-w-xl mx-auto">
          The bigger outcome is not just technical knowledge. It is the ability to make better website decisions with less dependence on guesswork, sales pages, and conflicting advice.
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {bigOutcome.map((item, i) => (
            <div key={i} className="bg-slate-800 rounded-xl p-5 text-center border border-slate-700">
              <div className="bg-blue-600/20 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                <SafeIcon icon={item.icon} className="text-blue-400 text-xl" />
              </div>
              <h4 className="font-bold text-white mb-1">{item.label}</h4>
              <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center">
        <Link to="/buy" className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition-colors text-lg">
          Buy the Book <SafeIcon icon={FiIcons.FiArrowRight} />
        </Link>
      </div>
    </div>
  );
}